import React, { useState } from 'react';
import Spinner from '@dashboard/components/Spinner';
import Callout from '@dashboard/components/Callout';
import CardListItem from '@dashboard/components/CardListItem';
import CardListItemElement from '@dashboard/components/CardListItemElement';
import Button from '@dashboard/components/Button';
import StatusBadge from '@/dashboard/components/StatusBadge';
import Dialog from '@dashboard/components/Dialog';
import Tooltip from '@dashboard/components/Tooltip';
import { formatDateTime } from '@/dashboard/lib/format-date';
import { WEBHOOK_STATUS } from '@dashboard/enums/webhook-events';
import WebhookDeleteDialog from './WebhookDeleteDialog';
import WebhookEditDialog from './WebhookEditDialog';
import WebhookTestButton from './WebhookTestButton';
import EventsLog from '@dashboard/components/EventsLog/EventsLog';
import Lozenge from '@/dashboard/components/Lozenge';

const WebhookTable = ({ data, isLoading, isError }) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState(null);

  const formatEvents = events => {
    if (!events || events.length === 0) return 'None';
    if (events.length === 1)
      return (
        <Tooltip label={events.join(', ')}>
          <Lozenge>{`${events[0]}`}</Lozenge>
        </Tooltip>
      );

    return (
      <Tooltip label={events.join(', ')}>
        <Lozenge>{`${events[0]} +${events.length - 1} more`}</Lozenge>
      </Tooltip>
    );
  };

  const formatSuccessRate = webhook => {
    if (!webhook || webhook.deliveryCount === 0) return 'N/A';
    const successRate = (webhook.successCount / webhook.deliveryCount) * 100;
    return `${Math.round(successRate)}%`;
  };

  const getStatusAppearance = status => {
    switch (status) {
      case WEBHOOK_STATUS.ACTIVE:
        return 'success';
      case WEBHOOK_STATUS.INACTIVE:
        return 'warning';
      case WEBHOOK_STATUS.FAILED:
        return 'error';
      default:
        return 'neutral';
    }
  };

  const formatLastActivity = webhook => {
    if (webhook.lastSuccessAt) {
      return formatDateTime(webhook.lastSuccessAt);
    }
    if (webhook.lastTriggeredAt) {
      return `Failed: ${formatDateTime(webhook.lastTriggeredAt)}`;
    }
    return 'Never';
  };

  const handleDeleteClick = webhook => {
    setSelectedWebhook(webhook);
    setDeleteDialogOpen(true);
  };

  const handleEditClick = webhook => {
    setSelectedWebhook(webhook);
    setEditDialogOpen(true);
  };

  return (
    <>
      {isLoading && <Spinner />}

      {isError && <Callout variant="error">Failed to load webhooks</Callout>}

      {data && data.webhooks?.length === 0 && !isLoading && !isError && (
        <Callout variant="info" padded={false}>
          No webhooks found. Create your first webhook to receive real-time notifications.
        </Callout>
      )}

      {data && data.webhooks?.length > 0 && (
        <div className="overflow-auto">
          <CardListItem variant="header" className="!mt-0">
            <CardListItemElement size="fill" className="!pl-0">
              Name
            </CardListItemElement>
            <CardListItemElement size="2xl">URL</CardListItemElement>
            <CardListItemElement size="xl">Events</CardListItemElement>
            <CardListItemElement size="medium">Status</CardListItemElement>
            <CardListItemElement size="large">Success rate</CardListItemElement>
            <CardListItemElement size="large">Last activity</CardListItemElement>
            <CardListItemElement size="3xl">Actions</CardListItemElement>
          </CardListItem>

          {data.webhooks.map(webhook => (
            <CardListItem key={webhook.id}>
              <CardListItemElement size="fill" className="!pl-0">
                <div>
                  <div className="font-medium">{webhook.name}</div>
                  <div className="text-xs text-gray-500">
                    Created {formatDateTime(webhook.createdAt)}
                  </div>
                </div>
              </CardListItemElement>

              <CardListItemElement size="2xl">
                <Tooltip label={webhook.url}>
                  <code className="text-xs bg-gray-100 px-2 py-1">{webhook.url}</code>
                </Tooltip>
              </CardListItemElement>

              <CardListItemElement size="xl">{formatEvents(webhook.events)}</CardListItemElement>

              <CardListItemElement size="medium">
                <StatusBadge
                  appearance={getStatusAppearance(webhook.status)}
                  label={webhook.status}
                />
              </CardListItemElement>

              <CardListItemElement size="large" align="center">
                {formatSuccessRate(webhook)}
              </CardListItemElement>

              <CardListItemElement size="large">
                <div className="text-sm">{formatLastActivity(webhook)}</div>
              </CardListItemElement>

              <CardListItemElement size="3xl" className="!pr-0">
                <div className="flex items-center gap-2">
                  <WebhookTestButton webhook={webhook} />

                  {webhook.integrationId && (
                    <Tooltip label="View logs" position="bottom">
                      <Dialog
                        title="Integration logs"
                        cancelButton={null}
                        confirmButton={null}
                        trigger={
                          <Button appearance="secondary" size="small">
                            Logs
                          </Button>
                        }
                        size="5xl"
                      >
                        <EventsLog integrationId={webhook.integrationId} hideResend={true} />
                      </Dialog>
                    </Tooltip>
                  )}

                  <Button
                    appearance="secondary"
                    size="small"
                    onClick={() => handleEditClick(webhook)}
                  >
                    Edit
                  </Button>
                  <Button
                    appearance="secondary"
                    size="small"
                    onClick={() => handleDeleteClick(webhook)}
                  >
                    Delete
                  </Button>
                </div>
              </CardListItemElement>
            </CardListItem>
          ))}
        </div>
      )}

      <WebhookDeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedWebhook(null);
        }}
        webhook={selectedWebhook}
      />

      <WebhookEditDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedWebhook(null);
        }}
        webhook={selectedWebhook}
      />
    </>
  );
};

export default WebhookTable;
